import type { LocalStore } from "./storage.js";
import { TRAFFIC_SOURCE, type TrafficSource } from "mindbricks-analytics-shared";

interface Touch {
  source: TrafficSource;
  landingPage: string;
  referrer: string | null;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  at: number;
}

const FIRST_KEY = "firstTouch";
const LAST_KEY = "lastTouch";

const SEARCH_HOSTS = [/google\./, /bing\./, /duckduckgo\./, /yahoo\./, /yandex\./, /baidu\./, /ecosia\./];
const SOCIAL_HOSTS = [
  /facebook\./,
  /fb\.com$/,
  /twitter\./,
  /x\.com$/,
  /instagram\./,
  /linkedin\./,
  /t\.co$/,
  /reddit\./,
  /tiktok\./,
  /pinterest\./,
  /youtube\./,
];

function classify(referrer: string | null, utm: { source?: string; medium?: string }): TrafficSource {
  const med = (utm.medium ?? "").toLowerCase();
  if (med === "cpc" || med === "ppc" || med === "paid" || med === "paidsearch") return TRAFFIC_SOURCE.PAID_SEARCH;
  if (med === "email") return TRAFFIC_SOURCE.EMAIL;
  if (med === "social") return TRAFFIC_SOURCE.SOCIAL;
  if (med === "organic") return TRAFFIC_SOURCE.ORGANIC_SEARCH;
  if (utm.source) return TRAFFIC_SOURCE.REFERRAL;

  if (!referrer) return TRAFFIC_SOURCE.DIRECT;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return TRAFFIC_SOURCE.UNKNOWN;
  }
  if (typeof location !== "undefined" && host === location.hostname) return TRAFFIC_SOURCE.DIRECT;
  if (SEARCH_HOSTS.some((re) => re.test(host))) return TRAFFIC_SOURCE.ORGANIC_SEARCH;
  if (SOCIAL_HOSTS.some((re) => re.test(host))) return TRAFFIC_SOURCE.SOCIAL;
  return TRAFFIC_SOURCE.REFERRAL;
}

type Utm = Pick<Touch, "utmSource" | "utmMedium" | "utmCampaign" | "utmTerm" | "utmContent">;

function readUtm(url: URL): Utm {
  const p = url.searchParams;
  const out: Utm = {};
  const s = p.get("utm_source");
  const m = p.get("utm_medium");
  const c = p.get("utm_campaign");
  const t = p.get("utm_term");
  const co = p.get("utm_content");
  if (s) out.utmSource = s;
  if (m) out.utmMedium = m;
  if (c) out.utmCampaign = c;
  if (t) out.utmTerm = t;
  if (co) out.utmContent = co;
  return out;
}

export class Attribution {
  private storage: LocalStore;
  private first: Touch;
  private last: Touch;
  private currentLandingPage: string;

  constructor(storage: LocalStore) {
    this.storage = storage;
    const url = typeof location !== "undefined" ? new URL(location.href) : new URL("http://unknown.local/");
    const referrer = typeof document !== "undefined" ? document.referrer || null : null;
    const utm = readUtm(url);
    const source = classify(referrer, { source: utm.utmSource, medium: utm.utmMedium });

    const newTouch: Touch = {
      source,
      landingPage: url.href,
      referrer,
      ...utm,
      at: Date.now(),
    };

    this.first = storage.getJson<Touch>(FIRST_KEY) ?? newTouch;
    if (!storage.getJson<Touch>(FIRST_KEY)) storage.setJson(FIRST_KEY, this.first);

    // The last-touch updates whenever a fresh visit brings new attribution
    // (a non-internal referrer or any utm parameter). Otherwise we keep the
    // previously stored last-touch so subsequent SPA navigations don't blow
    // it away.
    const hasUtm = !!(utm.utmSource || utm.utmMedium || utm.utmCampaign);
    const isExternal = referrer && (() => {
      try {
        return new URL(referrer).hostname !== url.hostname;
      } catch {
        return false;
      }
    })();
    if (hasUtm || isExternal) {
      this.last = newTouch;
      storage.setJson(LAST_KEY, this.last);
    } else {
      this.last = storage.getJson<Touch>(LAST_KEY) ?? newTouch;
      if (!storage.getJson<Touch>(LAST_KEY)) storage.setJson(LAST_KEY, this.last);
    }
    this.currentLandingPage = url.href;
  }

  decorateContext(): {
    utm?: Record<string, string>;
    trafficSource: TrafficSource;
    referrerDomain?: string;
    landingPage: string;
    firstTouchSource: TrafficSource;
    firstTouchLandingPage: string;
    lastTouchSource: TrafficSource;
    lastTouchLandingPage: string;
  } {
    const utm: Record<string, string> = {};
    if (this.last.utmSource) utm.source = this.last.utmSource;
    if (this.last.utmMedium) utm.medium = this.last.utmMedium;
    if (this.last.utmCampaign) utm.campaign = this.last.utmCampaign;
    if (this.last.utmTerm) utm.term = this.last.utmTerm;
    if (this.last.utmContent) utm.content = this.last.utmContent;

    let referrerDomain: string | undefined;
    if (this.last.referrer) {
      try {
        referrerDomain = new URL(this.last.referrer).hostname;
      } catch {
        // ignore
      }
    }

    const out: ReturnType<Attribution["decorateContext"]> = {
      trafficSource: this.last.source,
      landingPage: this.currentLandingPage,
      firstTouchSource: this.first.source,
      firstTouchLandingPage: this.first.landingPage,
      lastTouchSource: this.last.source,
      lastTouchLandingPage: this.last.landingPage,
    };
    if (Object.keys(utm).length) out.utm = utm;
    if (referrerDomain) out.referrerDomain = referrerDomain;
    return out;
  }
}
